import usePrivateAxios from '@/common/api/usePrivateAxios'
import { defaultTransformerResponse } from '@/common/constants/defaultTransformerResponse.constant'
import { Mapper } from '@/common/services/mapper'
import { MetaResponse } from '@/common/types/metaResponse'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

type UseMaintenanceForwardingsProps<T> = {
  filters?: {
    search?: string;
    page?: number;
    status?: string;
    equipmentId?: string;
    branchId?: string;
    checkinEntryType?: string;
    establishmentChoiceEntryType?: string;
    financialReviewEntryType?: string;
    directiveEntryType?: string;
    fiscalAssessmentEntryType?: string;
    checkoutEntryType?: string;
    actualEntry?: { relation?: string; entry_klass?: string | null; status?: string | null } | null;
  };
  mapper?: (data: any) => T;
  enabled?: boolean;
}

export const useMaintenanceForwardings = <T,>({
  filters: {
    search = '',
    page = 1,
    status,
    equipmentId,
    branchId,
    checkinEntryType,
    establishmentChoiceEntryType,
    financialReviewEntryType,
    directiveEntryType,
    fiscalAssessmentEntryType,
    checkoutEntryType,
    actualEntry,
  } = {},
  mapper = Mapper.default<T>(),
  enabled,
}: UseMaintenanceForwardingsProps<T>) => {
  const privateAxios = usePrivateAxios()

  const { data: { data, meta } = defaultTransformerResponse<T>(), ...rest } = useQuery({
    queryKey: [
      'maintenanceForwardings',
      search,
      page,
      status,
      equipmentId,
      branchId,
      checkinEntryType,
      establishmentChoiceEntryType,
      financialReviewEntryType,
      directiveEntryType,
      fiscalAssessmentEntryType,
      checkoutEntryType,
      actualEntry?.relation,
      actualEntry?.entry_klass,
      actualEntry?.status,
    ],
    queryFn: () => {
      return privateAxios.get(`/maintenance/forwardings`, {
        params: {
          search,
          page,
          'q[status_eq]': status,
          'q[equipment_id_eq]': equipmentId,
          'q[branch_id_eq]': branchId,
          'q[checkin_entry_type_eq]': checkinEntryType,
          'q[establishment_choice_entry_type_eq]': establishmentChoiceEntryType,
          'q[financial_review_entry_type_eq]': financialReviewEntryType,
          'q[directive_entry_type_eq]': directiveEntryType,
          'q[fiscal_assessment_entry_type_eq]': fiscalAssessmentEntryType,
          'q[checkout_entry_type_eq]': checkoutEntryType,
          'q[actual_entry][relation]': actualEntry?.relation,
          'q[actual_entry][entry_klass]': actualEntry?.entry_klass,
          'q[actual_entry][status]': actualEntry?.status,
        },
      })
    },
    select: (data: { data: { data: any[]; meta: MetaResponse } }) => {
      return {
        data: mapper(data?.data?.data),
        meta: data?.data?.meta,
      }
    },
    placeholderData: keepPreviousData,
    enabled: typeof enabled === 'boolean' ? enabled : true,
  });

  return { data, meta, ...rest }
}
